// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { isFleetChatEnabled } from './config';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from './context/AuthContext';
import { getConfigValue, getPortalLoginUrl, getPortalSsoStartUrl, safePortalNextUrl } from './config';

// Layout
import Layout from './components/Layout';
import MaintenanceBanner from './components/MaintenanceBanner';

// Core pages - eagerly loaded for instant navigation
import LoginPage from './pages/LoginPage';
import RegionsPage from './pages/RegionsPage';
import StargatesPage from './pages/StargatesPage';
import StargateDetailPage from './pages/StargateDetailPage';
import HttpViewLeakRedirect, { HttpViewNotFoundOrRecover } from './components/HttpViewLeakRedirect';

// Contexts
import { MqttActivityProvider } from './context/MqttActivityContext';
import { MqttDebugProvider } from './context/MqttDebugContext';
import { SharedMqttProvider } from './context/SharedMqttProvider';
import { StargatesProvider } from './context/StargatesContext';
import { ThemeContextProvider } from './context/ThemeContext';
import { FirmwareUpdateProvider } from './context/FirmwareUpdateContext';
import { ContainerPullProvider } from './context/ContainerPullContext';
import { AlertsProvider } from './context/AlertsContext';
import FirmwareUpdateAlertsBridge from './components/FirmwareUpdateAlertsBridge';
import SessionWarningModal from './components/SessionWarningModal';

// Heavy/infrequent pages - lazy loaded (cached after first load)
const DebugPage = lazy(() => import('./pages/DebugPage'));
const LicensesPage = lazy(() => import('./pages/LicensesPage'));
const SecurityReportPage = lazy(() => import('./pages/SecurityReportPage'));
const GuacamoleAdminPage = lazy(() => import('./pages/GuacamoleAdminPage'));
const GuacamoleConnectionsPage = lazy(() => import('./pages/GuacamoleConnectionsPage'));
const RemoteDesktopPage = lazy(() => import('./pages/RemoteDesktopPage'));
const RegistryPage = lazy(() => import('./pages/RegistryPage'));
const MassFirmwareUpdatePage = lazy(() => import('./pages/MassFirmwareUpdatePage'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const NetworkMetricsPage = lazy(() => import('./pages/NetworkMetricsPage'));
const MqttConnectPage = lazy(() => import('./pages/MqttConnectPage'));
const BackupPage = lazy(() => import('./pages/BackupPage'));
const HostMetricsPage = lazy(() => import('./pages/HostMetricsPage'));
const TopologyPage = lazy(() => import('./pages/TopologyPage'));
const EdgeDevicesPage = lazy(() => import('./pages/EdgeDevicesPage'));
const HelpVideosPage = lazy(() => import('./pages/HelpVideosPage'));
const FleetChatPage = lazy(() => import('./pages/FleetChatPage'));

// Full-screen intro video splash shown after login
const IntroSplash = ({ onFinished }) => {
  const videoRef = useRef(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => onFinished());
    }
  }, [onFinished]);

  const handleVideoEnd = useCallback(() => {
    setFading(true);
    setTimeout(onFinished, 500);
  }, [onFinished]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
        pointerEvents: fading ? 'none' : 'auto',
      }}
    >
      <video
        ref={videoRef}
        data-testid="intro-video"
        src="/isolated-networks-logo.mp4"
        muted
        playsInline
        onEnded={handleVideoEnd}
        onError={onFinished}
        style={{
          maxWidth: '80vw',
          maxHeight: '80vh',
          objectFit: 'contain',
          // Feather the rectangular video edges into the black backdrop so the
          // hard straight boundary between the frame and the page background is
          // no longer visible. Two axis gradients intersected give an opaque
          // interior that fades to transparent in a thin band at all four edges.
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, #000 7%, #000 93%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 7%, #000 93%, transparent 100%)',
          maskImage:
            'linear-gradient(to right, transparent 0%, #000 7%, #000 93%, transparent 100%), linear-gradient(to bottom, transparent 0%, #000 7%, #000 93%, transparent 100%)',
          WebkitMaskComposite: 'source-in',
          maskComposite: 'intersect',
        }}
      />
    </Box>
  );
};

const SsoRedirectSpinner = ({ breakGlass }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress />
    {breakGlass ? (
      <Box component="a" href="/login?breakglass=1" sx={{ color: 'primary.main', fontSize: 14 }}>
        Use local login
      </Box>
    ) : null}
  </Box>
);

const SsoLogoutPage = () => {
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('firmwareToken');
    window.dispatchEvent(new Event('user-logout'));
    const next = new URLSearchParams(window.location.search).get('next');
    window.location.replace(safePortalNextUrl(next));
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

const LoginRoute = () => {
  const { currentUser, isLoggingOut } = useAuth();
  const location = useLocation();
  if (currentUser) {
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  const ssoEnabled = getConfigValue('sso.enabled', true);
  const breakGlass = getConfigValue('sso.breakGlassLogin', true);
  const params = new URLSearchParams(location.search);
  const isBreakGlass = params.get('breakglass') === '1' || params.get('breakglass') === 'true';

  // logout() is (or may still be) navigating the browser away on its own —
  // firing our own redirect here to an auto-RELOGIN url can win that race
  // (last window.location.replace() call wins) and silently sign the user
  // back in. Render the neutral spinner and let logout()'s own navigation
  // land instead. See AuthContext.logout() / isLoggingOut.
  if (ssoEnabled && !(breakGlass && isBreakGlass) && !isLoggingOut) {
    const returnTo = `${window.location.origin}/`;
    const portalSso = getPortalSsoStartUrl(returnTo) || getPortalLoginUrl();
    if (portalSso) {
      window.location.replace(portalSso);
      return <SsoRedirectSpinner />;
    }
  }

  return <LoginPage />;
};

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, isLoggingOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    // Same race as LoginRoute above: logout() already cleared currentUser
    // and is navigating away itself — don't compete with it, and don't
    // client-side Navigate either (that would just flash /login before
    // logout()'s own navigation actually lands).
    if (isLoggingOut) {
      return <SsoRedirectSpinner />;
    }

    const ssoEnabled = getConfigValue('sso.enabled', true);
    const breakGlass = getConfigValue('sso.breakGlassLogin', true);
    const returnTo = `${window.location.origin}/`;
    const portalSso = getPortalSsoStartUrl(returnTo) || getPortalLoginUrl();
    if (ssoEnabled && portalSso) {
      const guardKey = 'sso_protected_redirect_guard';
      const last = sessionStorage.getItem(guardKey);
      if (!last || Date.now() - Number(last) > 8000) {
        sessionStorage.setItem(guardKey, String(Date.now()));
        window.location.replace(portalSso);
      }
      return <SsoRedirectSpinner breakGlass={breakGlass} />;
    }

    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search + location.hash }}
        replace
      />
    );
  }

  return children;
};

function App() {
  const { currentUser, loading } = useAuth();
  const [globalError, setGlobalError] = useState(null);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  const dismissIntro = useCallback(() => setShowIntro(false), []);

  // Prefetch the heavy, code-split page chunks in the background once the user is
  // authenticated and the browser is idle. Navigating to these pages (Network
  // Metrics, Topology, Registry, etc.) then mounts an already-downloaded chunk
  // instead of waiting on a multi-hundred-KB download — making nav feel instant.
  // Webpack dedupes these with the React.lazy() imports (same chunk).
  useEffect(() => {
    if (!currentUser) return undefined;
    const prefetch = () => {
      import('./pages/NetworkMetricsPage');
      import('./pages/TopologyPage');
      import('./pages/RegistryPage');
      import('./pages/EdgeDevicesPage');
      import('./pages/MassFirmwareUpdatePage');
      import('./pages/HostMetricsPage');
    };
    let idleId;
    let timerId;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
      timerId = setTimeout(prefetch, 2000);
    }
    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function')
        window.cancelIdleCallback(idleId);
      if (timerId != null) clearTimeout(timerId);
    };
  }, [currentUser]);

  useEffect(() => {
    const onLogin = () => {
      // Play the intro splash on every login — both after a deliberate
      // logout/login and after re-login following an inactivity timeout.
      // Clear the timeout marker (it only gated this video) for hygiene.
      sessionStorage.removeItem('sessionTimedOut');
      setShowIntro(true);
    };
    window.addEventListener('login', onLogin);
    return () => window.removeEventListener('login', onLogin);
  }, []);

  useEffect(() => {
    // Handler for uncaught errors
    const handleError = (event) => {
      // Don't handle MQTT connection errors here as they're already handled by MqttDebugContext
      if (event.error && event.error.name === 'MqttConnectionError') {
        console.warn('MQTT connection error handled by MqttDebugContext:', event.error.message);
        return;
      }

      const errorMessage = event.message || (event.error ? String(event.error) : 'Unknown error');
      console.error('Uncaught error:', errorMessage, event.error);
      setGlobalError(errorMessage);

      // Auto-dismiss error after 5 seconds
      setTimeout(() => {
        setGlobalError(null);
      }, 5000);
    };

    // Handler for unhandled promise rejections
    const handleRejection = (event) => {
      // Don't handle MQTT connection errors here as they're already handled by MqttDebugContext
      if (event.reason && event.reason.name === 'MqttConnectionError') {
        console.warn(
          'MQTT connection rejection handled by MqttDebugContext:',
          event.reason.message
        );
        return;
      }

      // Don't show error banner for canceled requests (component unmount)
      if (
        event.reason &&
        (event.reason.code === 'ERR_CANCELED' || event.reason.name === 'CanceledError')
      ) {
        return;
      }

      // Check if this is an API error with retry information
      const error = event.reason;
      const retryCount = error?._retryCount || 0;
      const isPersistentFailure = error?._isPersistentFailure || false;

      // Only show error banner for persistent failures (after multiple retries)
      // For transient errors (502/503/504), we retry silently in the background
      if (error?.response) {
        const status = error.response.status;
        const isRetryableError = [502, 503, 504].includes(status);

        // For transient errors without retry metadata (from remoteApiCancelable, etc.),
        // suppress them silently as they're being retried by the component's own retry logic
        if (isRetryableError && (!isPersistentFailure || retryCount === 0)) {
          // Transient error that will be retried - don't show banner or log as error
          // Only log in development mode for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `Transient error ${status} detected, retrying silently (attempt ${retryCount})`
            );
          }
          return;
        }
      }

      const errorMessage = event.reason
        ? event.reason.message || String(event.reason)
        : 'Unhandled promise rejection';

      // Only log and show banner for persistent failures or non-retryable errors
      if (
        isPersistentFailure ||
        !error?.response ||
        ![502, 503, 504].includes(error.response?.status)
      ) {
        console.error('Unhandled rejection:', errorMessage, event.reason);
        setGlobalError(errorMessage);

        // Auto-dismiss error after 5 seconds
        setTimeout(() => {
          setGlobalError(null);
        }, 5000);
      } else {
        // Transient error that we're suppressing - only log in dev mode
        if (process.env.NODE_ENV === 'development') {
          console.warn('Suppressing transient error:', errorMessage);
        }
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  useEffect(() => {
    // Track whether the user intentionally logged out so we can suppress
    // the "session timed out" banner for 401s caused by clearing the token
    let intentionalLogout = false;

    // Listen for intentional logout - user clicked "Logout" button
    const handleUserLogout = () => {
      intentionalLogout = true;
      setSessionTimeout(false); // Clear any existing timeout banner
      // Reset after a window so future real timeouts still show the banner
      setTimeout(() => {
        intentionalLogout = false;
      }, 15000);
    };

    // Listen for session-timeout event (from axios interceptor or AuthContext inactivity check)
    const handleSessionTimeout = () => {
      // Only show the banner if this was NOT triggered by an intentional logout
      if (!intentionalLogout) {
        setSessionTimeout(true);
      }
    };

    // Listen for login event to clear session timeout banner immediately
    const handleLogin = () => {
      setSessionTimeout(false);
      intentionalLogout = false;
    };

    window.addEventListener('user-logout', handleUserLogout);
    window.addEventListener('session-timeout', handleSessionTimeout);
    window.addEventListener('login', handleLogin);

    return () => {
      window.removeEventListener('user-logout', handleUserLogout);
      window.removeEventListener('session-timeout', handleSessionTimeout);
      window.removeEventListener('login', handleLogin);
    };
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeContextProvider>
      <MaintenanceBanner />
      <SharedMqttProvider>
        <MqttActivityProvider>
          <MqttDebugProvider>
            <StargatesProvider>
              <AlertsProvider>
                <FirmwareUpdateProvider>
                  <FirmwareUpdateAlertsBridge />
                  <ContainerPullProvider>
                    {showIntro && <IntroSplash onFinished={dismissIntro} />}
                    <SessionWarningModal />
                    {sessionTimeout && (
                      <div
                        style={{
                          background: 'rgba(138, 148, 168, 0.08)',
                          color: '#fff',
                          padding: '16px',
                          fontWeight: 'bold',
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          width: '100%',
                          zIndex: 10000,
                          textAlign: 'center',
                          fontSize: '1.2rem',
                        }}
                      >
                        Session has timed out, please log back in.
                      </div>
                    )}
                    {globalError && (
                      <div
                        style={{
                          background: 'red',
                          color: 'white',
                          padding: '12px',
                          fontWeight: 'bold',
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          width: '100%',
                          zIndex: 9999,
                        }}
                      >
                        Uncaught Error: {globalError}
                      </div>
                    )}
                    <Routes>
                      <Route path="/login" element={<LoginRoute />} />
                      <Route path="/sso-logout" element={<SsoLogoutPage />} />

                      {/* Remote desktop page - full screen with iframe, auto-refreshes tokens */}
                      <Route
                        path="/remote-loading/:connectionId/:connectionName"
                        element={
                          <ProtectedRoute>
                            <Suspense
                              fallback={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100vh',
                                  }}
                                >
                                  <CircularProgress />
                                </Box>
                              }
                            >
                              <RemoteDesktopPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      {/* Device leak routes — outside Layout so sidebar never renders */}
                      <Route path="/rui/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/home/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/wuilib/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/permissions/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/system/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/common/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/PMLib/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/OSS/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/ssm/*" element={<HttpViewLeakRedirect />} />
                      <Route path="/LOGOUT.cmd" element={<HttpViewLeakRedirect />} />
                      <Route path="/logout.cmd" element={<HttpViewLeakRedirect />} />

                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<RegionsPage />} />
                        <Route path="regions/:regionId" element={<StargatesPage />} />
                        <Route path="stargates/:stargateId" element={<StargateDetailPage />} />
                        <Route path="debug" element={<DebugPage />} />
                        <Route path="network-metrics" element={<NetworkMetricsPage />} />
                        <Route path="mass-firmware-update" element={<MassFirmwareUpdatePage />} />
                        <Route path="licenses" element={<LicensesPage />} />
                        <Route path="security" element={<SecurityReportPage />} />
                        <Route path="remote-admin" element={<GuacamoleAdminPage />} />
                        <Route path="remote-connections" element={<GuacamoleConnectionsPage />} />

                        {/* Backward compatibility redirects */}
                        <Route
                          path="guacamole-admin"
                          element={<Navigate to="/remote-admin" replace />}
                        />
                        <Route
                          path="guacamole-connections"
                          element={<Navigate to="/remote-connections" replace />}
                        />
                        <Route path="registry" element={<RegistryPage />} />
                        <Route path="api-docs" element={<ApiDocsPage />} />
                        <Route path="mqtt-connect" element={<MqttConnectPage />} />
                        <Route path="backup" element={<BackupPage />} />
                        <Route path="host-metrics" element={<HostMetricsPage />} />
                        <Route path="topology" element={<TopologyPage />} />
                        <Route path="edge-latency" element={<EdgeDevicesPage />} />
                        <Route path="help" element={<HelpVideosPage />} />
                        <Route
                          path="fleet-chat"
                          element={
                            isFleetChatEnabled() ? <FleetChatPage /> : <Navigate to="/" replace />
                          }
                        />

                        {/* Inner catch-all: handles unknown paths that matched the "/" parent.
                      Uses the same allowlist logic — redirects to proxy if active session. */}
                        <Route path="*" element={<HttpViewNotFoundOrRecover />} />
                      </Route>
                    </Routes>
                  </ContainerPullProvider>
                </FirmwareUpdateProvider>
              </AlertsProvider>
            </StargatesProvider>
          </MqttDebugProvider>
        </MqttActivityProvider>
      </SharedMqttProvider>
    </ThemeContextProvider>
  );
}

export default App;
